/**
 * Membership cart-removal exit survey trigger (Trigger #1, ENG-14334).
 *
 * Client-side decision logic for whether to show the SurveyMonkey exit survey when a
 * membership subscription is removed from the cart. Kept as a small, framework-agnostic
 * module (mirrors the `fetchAnalytics.js` pattern) so both `store-vue` and `order-vue`
 * share one source of truth.
 *
 * Responsibilities:
 *   - honor the `membership_removal_survey` feature flag (passed in by the caller),
 *   - enforce a suppression window so a user is not re-shown the survey for 24h,
 *   - decide whether the caller should open the survey dialog.
 *
 * It does NOT render anything and does NOT talk to SurveyMonkey — the caller mounts the
 * `DpSurveySubscriptionModal` overlay (which lazy-loads the survey iframe) when this
 * returns true. Suppression is keyed on OPEN, not submission (submission is unobservable
 * cross-origin).
 */

/** The SurveyMonkey survey shown for Trigger #1 (free tier, no metadata/parameters). */
export const MEMBERSHIP_REMOVAL_SURVEY_URL = 'https://www.surveymonkey.com/r/XPBJFRR';

/** localStorage key holding the timestamp (ms) the survey was last shown. */
export const SURVEY_SUPPRESS_KEY = 'dp_membership_removal_survey_last_shown';

/** Suppression window: do not re-show the survey within this many milliseconds (24h). */
export const SURVEY_SUPPRESS_MS = 24 * 60 * 60 * 1000;

/**
 * Whether the survey is currently suppressed by the 24h window.
 *
 * Fails OPEN: if localStorage is unavailable or the stored value is unusable, we treat the
 * survey as NOT suppressed (better to risk showing it than to silently never show it).
 *
 * @param {number} [now=Date.now()] - Current time in ms (injectable for tests).
 * @returns {boolean}
 */
export function isSurveySuppressed(now = Date.now()) {
  try {
    const raw = window.localStorage.getItem(SURVEY_SUPPRESS_KEY);
    if (!raw) {
      return false;
    }
    const lastShown = Number(raw);
    if (!Number.isFinite(lastShown)) {
      return false;
    }
    return now - lastShown < SURVEY_SUPPRESS_MS;
  } catch (e) {
    // Private mode / storage disabled — fail open.
    return false;
  }
}

/**
 * Record that the survey was shown now, starting the 24h suppression window.
 *
 * Best-effort: storage failures are swallowed (the survey simply won't be suppressed next
 * time, which is acceptable and non-blocking).
 *
 * @param {number} [now=Date.now()] - Current time in ms (injectable for tests).
 */
export function markSurveyShown(now = Date.now()) {
  try {
    window.localStorage.setItem(SURVEY_SUPPRESS_KEY, String(now));
  } catch (e) {
    // No-op: suppression is a nicety, never a blocker.
  }
}

/**
 * Decide whether the membership cart-removal survey should be shown.
 *
 * When it returns true it has ALREADY recorded the show (suppression window starts now), so
 * the caller must open the dialog. This keeps the "at most once per window" guarantee even
 * if the caller mounts the overlay asynchronously.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - The evaluated `membership_removal_survey` flag value.
 * @returns {boolean} true if the caller should open the survey dialog.
 */
export function shouldShowSurvey({ enabled } = {}) {
  if (!enabled) {
    return false;
  }
  if (isSurveySuppressed()) {
    return false;
  }
  markSurveyShown();
  return true;
}
